import {
  Accordion,
  AccordionItem,
  Icon,
  useWindowSize,
  style
} from '@do/walrus';
import * as React from 'react';
import { useEffect } from 'react';
import styled, { css } from 'styled-components';
import {
  getAppId,
  getCategory,
  getCreated,
  getDeveloperName,
  getIcon,
  getName,
  getOsVersion,
  getSummary,
  getType,
  getVersion
} from '../../selectors';
import OneClickResource from '../OneClickResource/OneClickResource';
import { FeaturedTutorial, TutorialsGrid } from '../Tutorials';
import copy from '../copy';
import linksIcon from '../icons/links.svg?sprite';
import supportIcon from '../icons/support.svg?sprite';
import AdditionalLinks from './AdditionalLinks';
import Description from './Description';
import GettingStarted, { GETTING_STARTED_HASH } from './GettingStarted';
import SoftwareIncluded from './SoftwareIncluded';
import SupportDetails, { SUPPORT_DETAILS_HASH } from './SupportDetails';

const AccordionContainer = styled.div<{ maxAccordionHeight: number }>`
  color: ${style.colors.grey.darkest};

  ${({ maxAccordionHeight }) =>
    maxAccordionHeight &&
    css`
      max-height: ${maxAccordionHeight}px;
    `}
`;

const HeaderContainer = styled.div`
  position: relative;
  padding-bottom: ${style.vars.space['6']};
  border-bottom: 1px solid ${style.colors.shadesOfGrey['4']};
`;

interface TrackingProps {
  developer?: string;
  resource?: string;
  safeName?: string;
  type?: string;
}

interface Props {
  app: any;
  tutorialsData: {
    tutorialsLink: string;
    featuredTutorials: FeaturedTutorial[];
  };
  isStaffPick?: boolean;
  createButtonUrl?: string;
  onClickCreateButton?: (props: TrackingProps, e: React.MouseEvent) => void;
}

const DetailAccordion: React.FC<Props> = ({
  app,
  tutorialsData,
  isStaffPick
}) => {
  const name = getName(app);
  const summary = getSummary(app);
  const category = getCategory(app);
  const icon = getIcon(app);
  const developerName = getDeveloperName(app);
  const type = getType(app);
  const { innerHeight: maxAccordionHeight } = useWindowSize();

  useEffect(() => {
    window?.Prism?.highlightAll?.();
  }, []);

  useEffect(() => {
    const hash = window?.location?.hash;
    let sectionBtn;

    if (hash === `#${GETTING_STARTED_HASH}`) {
      sectionBtn = document.querySelector(
        `[aria-label='${copy.detailAccordion.getStartedInstructions}']`
      );
    } else if (hash === `#${SUPPORT_DETAILS_HASH}`) {
      sectionBtn = document.querySelector(
        `[aria-label='${copy.detailAccordion.contactSupport}']`
      );
    }

    if (!!sectionBtn && sectionBtn.getAttribute('aria-expanded') === 'false') {
      sectionBtn.dispatchEvent(
        new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: false
        })
      );
    }

    return;
  }, []);

  return (
    <AccordionContainer maxAccordionHeight={maxAccordionHeight}>
      <Accordion id="app-detail-accordion">
        <AccordionItem
          title={copy.detailAccordion.appOverview}
          icon={<Icon icon="overview-info" large />}
        >
          <HeaderContainer>
            <OneClickResource
              isHorizontal={true}
              name={name}
              icon={icon}
              appID={getAppId(app)}
              version={getVersion(app)}
              osVersion={getOsVersion(app)}
              developer={developerName}
              created={getCreated(app)}
              summary={summary}
              category={category}
              type={type}
              isStaffPick={isStaffPick}
            />
          </HeaderContainer>
          <Description app={app} />
          <SoftwareIncluded app={app} isInIframe />
        </AccordionItem>
        <AccordionItem
          title={copy.detailAccordion.getStartedInstructions}
          icon={<Icon icon="overview-getting-started" large />}
        >
          <GettingStarted app={app} isInIframe />
        </AccordionItem>
        {tutorialsData?.tutorialsLink && tutorialsData?.featuredTutorials && (
          <AccordionItem
            title={copy.detailAccordion.digitalOceanCommunityTutorials}
            icon={<Icon icon="overview-tutorials" large />}
          >
            <TutorialsGrid
              name={name}
              tutorialsLink={tutorialsData.tutorialsLink}
              featuredTutorials={tutorialsData.featuredTutorials}
            />
          </AccordionItem>
        )}
        <AccordionItem
          title={copy.detailAccordion.contactSupport}
          icon={<Icon xlinkHrefPrefix="" icon={supportIcon.id} large blue />}
        >
          <SupportDetails app={app} />
        </AccordionItem>
        <AccordionItem
          title={copy.detailAccordion.additionalLinks}
          icon={<Icon xlinkHrefPrefix="" icon={linksIcon.id} large blue />}
        >
          <AdditionalLinks app={app} />
        </AccordionItem>
      </Accordion>
    </AccordionContainer>
  );
};

export default DetailAccordion;
